// tippi/src/pages/common/FeedbackPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supportApi } from '../../firebase/functionsService';
import { useMyCases, useCaseMessages } from '../../hooks/useSupportCases';
import { useAuth } from '../../contexts/AuthContext';
import { deviceLabel } from '../../utils/deviceLabel';
import { COLORS, FONTS } from '../../styles/tokens';

const KINDS = [['bug', 'Feil'], ['idea', 'Forslag'], ['question', 'Spørsmål']];
const STATUS_TEXT = { new: 'Mottatt', in_progress: 'Under arbeid', waiting: 'Venter på deg', resolved: 'Løst' };
const fmt = (v) => v?.toDate?.().toLocaleString('nb-NO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) ?? '';
const errText = (e, fallback) => {
  const msg = (e?.message || '').replace(/^functions\/[\w-]+:\s*/, '').trim();
  return msg && !/^internal$/i.test(msg) ? msg : fallback;
};

// Felles for sjåfør og bestiller: send tilbakemelding, se egne saker og samtalen med Tippi support.
export default function FeedbackPage() {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const { cases } = useMyCases();
  const [kind, setKind] = useState('bug');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy || !text.trim()) return;
    setBusy(true); setError(''); setSent(false);
    try {
      const res = await supportApi.createFeedback({
        kind, text: text.trim(), role: effectiveRole, page: window.location.pathname, device: deviceLabel(),
      });
      setText(''); setSent(true); setOpenId(res?.caseId ?? null);
    } catch (err) {
      setError(errText(err, 'Kunne ikke sende. Prøv igjen.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.root}>
      <div style={styles.page}>
        <button type="button" onClick={() => navigate(-1)} style={styles.back}>← Tilbake</button>
        <h1 style={styles.title}>Gi tilbakemelding</h1>
        <p style={styles.lead}>Fant du en feil, har du et forslag, eller lurer du på noe? Vi svarer her i appen.</p>

        <form onSubmit={submit} style={styles.card}>
          <div role="group" aria-label="Type" style={styles.chips}>
            {KINDS.map(([v, l]) => (
              <button key={v} type="button" aria-pressed={kind === v} onClick={() => setKind(v)} style={{ ...styles.chip, ...(kind === v ? styles.chipOn : {}) }}>{l}</button>
            ))}
          </div>
          <textarea
            aria-label="Din tilbakemelding" value={text} onChange={e => { setText(e.target.value); setSent(false); }}
            rows={5} maxLength={2000} style={styles.textarea}
            placeholder={kind === 'bug' ? 'Hva gjorde du, og hva skjedde? Hva forventet du?' : 'Skriv her…'}
          />
          <p style={styles.hint}>Vi legger automatisk ved hvilken side du var på og hva slags enhet du bruker, så det blir lettere å finne feilen.</p>
          {error && <p role="alert" style={styles.error}>{error}</p>}
          {sent && <p role="status" style={styles.ok}>Takk! Vi har mottatt tilbakemeldingen og svarer her i appen.</p>}
          <button type="submit" className="tippi-btn" disabled={busy || !text.trim()} style={{ ...styles.send, opacity: busy || !text.trim() ? 0.5 : 1 }}>
            {busy ? 'Sender…' : 'Send'}
          </button>
        </form>

        {cases.length > 0 && <h2 style={styles.h2}>Mine henvendelser</h2>}
        {cases.map(c => (
          <div key={c.id} style={styles.caseCard}>
            <button type="button" aria-expanded={openId === c.id} onClick={() => setOpenId(openId === c.id ? null : c.id)} style={styles.caseHead}>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={styles.caseTitle}>{c.title}</span>
                <span style={styles.caseMeta}>{fmt(c.lastMessageAt)}</span>
              </span>
              <span style={{ ...styles.status, ...(c.status === 'waiting' ? styles.statusAttention : {}) }}>{STATUS_TEXT[c.status] ?? c.status}</span>
            </button>
            {openId === c.id && <Thread caseItem={c} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Thread({ caseItem }) {
  const { messages } = useCaseMessages(caseItem.id);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const resolved = caseItem.status === 'resolved';

  async function send(e) {
    e.preventDefault();
    if (busy || !reply.trim()) return;
    setBusy(true); setError('');
    try {
      await supportApi.addCaseMessage({ caseId: caseItem.id, text: reply.trim() });
      setReply('');
    } catch (err) {
      setError(errText(err, 'Kunne ikke sende. Prøv igjen.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="region" aria-label="Samtale" style={styles.thread}>
      {messages.map(m => (
        <div key={m.id} style={{ ...styles.msg, ...(m.from === 'admin' ? styles.msgAdmin : styles.msgMine) }}>
          <div style={styles.msgWho}>{m.from === 'admin' ? 'Tippi support' : 'Du'} · {fmt(m.at)}</div>
          <div style={styles.msgText}>{m.text}</div>
        </div>
      ))}
      {resolved ? (
        <p style={styles.hint}>Saken er løst. Trenger du mer hjelp, send en ny tilbakemelding over.</p>
      ) : (
        <form onSubmit={send} style={{ marginTop: 8 }}>
          <textarea aria-label="Svar" value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={2000} style={styles.textarea} placeholder="Skriv svar…" />
          {error && <p role="alert" style={styles.error}>{error}</p>}
          <button type="submit" disabled={busy || !reply.trim()} style={{ ...styles.sendSmall, opacity: busy || !reply.trim() ? 0.5 : 1 }}>Send svar</button>
        </form>
      )}
    </div>
  );
}

const styles = {
  root:   { minHeight: '100vh', background: COLORS.bg, paddingBottom: 90 },
  page:   { maxWidth: 560, margin: '0 auto', padding: '16px 16px 32px' },
  back:   { background: 'none', border: 'none', color: COLORS.textMid, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 8 },
  title:  { fontFamily: FONTS.display, fontSize: 30, fontWeight: 700, color: COLORS.textDark, margin: '0 0 6px' },
  lead:   { fontSize: 14, color: COLORS.textMid, margin: '0 0 16px', lineHeight: 1.45 },
  card:   { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 14 },
  chips:  { display: 'flex', gap: 8, marginBottom: 12 },
  chip:   { padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  chipOn: { borderColor: COLORS.primary, background: COLORS.primary, color: COLORS.onPrimary },
  textarea: { width: '100%', boxSizing: 'border-box', borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: '10px 12px', fontSize: 15, fontFamily: 'inherit', resize: 'vertical', background: COLORS.white, color: COLORS.textDark },
  hint:   { fontSize: 12, color: COLORS.textLight, margin: '8px 2px 0', lineHeight: 1.4 },
  error:  { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 10, fontSize: 13, margin: '10px 0 0' },
  ok:     { background: COLORS.successBg, color: COLORS.successText, padding: '8px 12px', borderRadius: 10, fontSize: 13, margin: '10px 0 0' },
  send:   { display: 'block', width: '100%', marginTop: 12, padding: '13px 8px', borderRadius: 26, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  sendSmall: { marginTop: 8, padding: '10px 18px', borderRadius: 20, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  h2:     { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: '24px 0 10px' },
  caseCard: { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, marginBottom: 10, overflow: 'hidden' },
  caseHead: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textDark },
  caseTitle: { display: 'block', fontWeight: 700, fontSize: 15 },
  caseMeta:  { display: 'block', fontSize: 12, color: COLORS.textLight, marginTop: 2 },
  status: { fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: COLORS.surfaceHover, color: COLORS.textMid, whiteSpace: 'nowrap' },
  statusAttention: { background: COLORS.warningBg, color: COLORS.warningText },
  thread: { borderTop: `1px solid ${COLORS.border}`, padding: 14 },
  msg:    { borderRadius: 12, padding: '8px 12px', marginBottom: 8 },
  msgMine:  { background: COLORS.bg },
  msgAdmin: { background: COLORS.successBg },
  msgWho: { fontSize: 11, color: COLORS.textLight, marginBottom: 2 },
  msgText: { fontSize: 14, color: COLORS.textDark, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
};
